import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useRouteLoaderData } from 'react-router';
import { useTypedParams, useTypedSearchParams, useTypedState } from 'react-router-typesafe-routes';
import ConversationList from '@/components/conversations/conversation-list/conversation-virtuoso-list';
import ConversationProviders from '@/components/conversations/conversation-providers';
import MessageStateIndicator from '@/components/conversations/message-state-indicator';
import SessionSettingMenu from '@/components/conversations/session-setting-menu';
import { WebSocketStateIndicator } from '@/components/conversations/web-socket-state-indicator';
import AppHeader from '@/components/layouts/page-layout/app-header';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import { useSessionMessages } from '@/hooks/sessions/use-session-messages';
import useResetScrollYOnFocus from '@/hooks/use-reset-scroll-y-on-focus';
import useRotateResize from '@/hooks/use-rotate-resize';
import useUserPlatform from '@/hooks/use-user-platform';
import { UserType } from '@/models/user/user-info';
import ROUTES from '@/routes';
import type { SessionMessagesLayoutLoaderReturnType } from '@/routes/messages/sessions/_layout.loader';
import { createConversationStore } from '@/stores/conversation';

interface Props {
  sessionId: number;
  layout?: 'full' | 'mini';
}

export function SessionMessages({ layout = 'full', sessionId }: Props) {
  const { t } = useTranslation();
  const layoutLoaderData = useRouteLoaderData<SessionMessagesLayoutLoaderReturnType>(ROUTES.MESSAGES.SESSIONS.$path());
  const [{ search: searchId }] = useTypedSearchParams(ROUTES.MESSAGES.SESSIONS);
  const { from: navigatedFrom } = useTypedState(ROUTES.MESSAGES.SESSIONS);
  const { session, friendInfo, isHistoryLoading } = useSessionMessages(sessionId, searchId);
  const isAdminSession = friendInfo?.UserInfo?.Type === UserType.Admin;
  const store = useMemo(
    () => createConversationStore('session', sessionId, isAdminSession, layout),
    [sessionId, isAdminSession],
  );
  const { isDesktop, isEmbedded } = useUserPlatform();
  const { dispatch } = useCommDispatcher();

  const promise = useMemo(() => layoutLoaderData?.promises || false, [sessionId]);

  useResetScrollYOnFocus();
  useRotateResize();

  const handleNavigateBack = () => {
    if (isEmbedded) {
      dispatch({
        event: 'exit-session',
        data: { type: 'session', id: sessionId },
      });

      if (navigatedFrom === 'comm') dispatch({ event: 'dismiss' });
    }
  };

  return (
    <PageLayout
      title={
        (friendInfo?.FriendInfo.RemarkName || friendInfo?.FriendInfo.FriendNickName || session?.NickName) ??
        t('nav.messages')
      }
      className='gap-0 pb-safe-or-0 has-[.ql-editor:focus]:mode-standalone:pb-0'>
      {layout === 'full' && (
        <AppHeader
          navigateBack={isDesktop ? undefined : Boolean(searchId || navigatedFrom === 'search') || true}
          onNavigateBack={handleNavigateBack}
          className='desktop:px-6 desktop:border-b border-base-200 mb-0'
          actions={
            <>
              <MessageStateIndicator />
              <WebSocketStateIndicator />
              <SessionSettingMenu
                type='session'
                id={sessionId}
              />
            </>
          }>
          {(session || friendInfo) && (
            <Link
              className='w-full desktop:w-fit'
              to={
                isDesktop
                  ? friendInfo?.UserInfo?.UID
                    ? ROUTES.CONTACT.USERS.BY_ID.$buildPath({
                        params: { userId: friendInfo?.UserInfo?.UID },
                      })
                    : ''
                  : ROUTES.MESSAGES.SESSIONS.$.SETTINGS.$path({ relative: true })
              }>
              <div className='flex w-full items-center gap-4'>
                <h3 className='line-clamp-1 flex-1 break-all text-base font-bold'>
                  {friendInfo?.FriendInfo.RemarkName || friendInfo?.FriendInfo.FriendNickName || session?.NickName}
                </h3>
              </div>
            </Link>
          )}
        </AppHeader>
      )}

      <ConversationProviders
        layout={layout}
        type='session'
        id={sessionId}
        store={store}>
        <LoadingLayout
          className='bg-base-100'
          promise={promise}>
          {() => (
            <ConversationList
              isLoading={isHistoryLoading}
              searchId={searchId}
              initialUnreadCount={layoutLoaderData?.initialUnreadCount}
            />
          )}
        </LoadingLayout>
      </ConversationProviders>
    </PageLayout>
  );
}

export default function SessionMessagesByIdRoute() {
  const { id } = useTypedParams(ROUTES.MESSAGES.SESSIONS);
  const { layout } = useTypedState(ROUTES.MESSAGES.SESSIONS);
  const { isEmbedded } = useUserPlatform();

  return (
    <SessionMessages
      layout={isEmbedded ? layout : 'full'}
      sessionId={id}
    />
  );
}
